import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import { Spin, Row, Col } from 'antd';
import pluralize from 'pluralize';

import {
  AssignmentStatusTypeEnum,
  AdminPagedAssignmentResponseModel,
} from 'api/adminApiClient';
import AssignmentCard from '../AssignmentCard';
import { getAssignments } from 'redux/reducers/assignmentSlice';
import { AssignmentTitleInfoModel } from 'api/backofficeApiClient';
import { assignmentDraftCreatedAtSelector } from 'redux/selectors/assignmentSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  isAdmin?: boolean;
};

export default function AssignmentsDraftSection(props: Props) {
  const { isAdmin } = props;
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const [assignments, setAssignments] = useState<AssignmentTitleInfoModel[]>(
    [],
  );
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const assignmentDraftCreatedAt = useAppSelector(
    assignmentDraftCreatedAtSelector,
  );

  useEffect(() => {
    initInfinityScroll();
  }, [assignmentDraftCreatedAt]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setAssignments([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetAssignments = useCallback(
    (result: PayloadAction<AdminPagedAssignmentResponseModel>) => {
      const loadedAssignments = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newAssignments = [...assignments, ...loadedAssignments];
      const hasMoreRows = newAssignments.length < totalCount;

      setHasMore(hasMoreRows);
      setAssignments(newAssignments);
      setTotalCount(totalCount);
    },
    [assignments],
  );

  const loadAssignments = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getAssignments({
          requestId,
          pageNumber,
          isAdminRole: isAdmin,
          statuses: [AssignmentStatusTypeEnum.Draft],
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getAssignments.fulfilled.match(result)) {
          onSetAssignments(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, isAdmin, onSetAssignments],
  );

  const getTotalCountMessage = () => {
    const requestWord = pluralize('request', totalCount);

    return `${totalCount} draft assignment ${requestWord}`;
  };

  const getNoAssignmentsMessage = () => {
    return 'You have no draft assignments at the moment.';
  };

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[10, 10]}>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountMessage()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      {getNoAssignmentsMessage()}
                    </span>
                  )}
                </>
              )}

              {isVisible && (
                <InfiniteScroll
                  pageStart={-1}
                  loadMore={loadAssignments}
                  hasMore={hasMore}
                  loader={<Spin key={0} />}
                  useWindow={false}
                  getScrollParent={() => infiniteScrollParentRef.current}
                >
                  <Row gutter={[20, 20]}>
                    {assignments.map((assignment) => (
                      <Col
                        key={assignment.assignmentId}
                        xs={24}
                        md={12}
                        className="d-flex"
                      >
                        <AssignmentCard assignment={assignment} />
                      </Col>
                    ))}
                  </Row>
                </InfiniteScroll>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
